/**
 * CustomerSelect Component - T105
 *
 * Searchable dropdown for selecting customers
 */
import { useState, useRef, useEffect } from 'react';
import { useCustomerSearch, type Customer } from '../../hooks/useCustomers';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface CustomerSelectProps {
  value: string | null;
  onChange: (customerId: string, customer: Customer) => void;
  selectedCustomer?: Customer | null;
  error?: string;
  disabled?: boolean;
}

export function CustomerSelect({
  value,
  onChange,
  selectedCustomer,
  error,
  disabled,
}: CustomerSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: customers, isLoading } = useCustomerSearch(search, 10);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (customer: Customer) => {
    onChange(customer.id, customer);
    setSearch('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('', null as unknown as Customer);
    setSearch('');
  };

  const displayValue = selectedCustomer
    ? `${selectedCustomer.customerNumber} - ${selectedCustomer.companyName}`
    : '';

  return (
    <div className="w-full" ref={containerRef}>
      <label className="block text-sm font-medium text-white/70 mb-1.5">
        Kunde *
      </label>
      <div className="relative">
        {/* Selected customer display / search input */}
        {value && selectedCustomer && !isOpen ? (
          <div
            onClick={() => !disabled && setIsOpen(true)}
            className={`
              w-full px-4 py-2.5 rounded-lg flex items-center justify-between
              bg-white/[0.03] border border-white/[0.08]
              text-white cursor-pointer
              hover:border-white/20 hover:bg-white/[0.05]
              transition-colors duration-200
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              ${error ? 'border-red-500/50' : ''}
            `}
          >
            <span>{displayValue}</span>
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="text-white/40 hover:text-white/70"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        ) : (
          <input
            ref={inputRef}
            type="text"
            placeholder="Kunde suchen..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            disabled={disabled}
            className={`
              w-full px-4 py-2.5 rounded-lg
              bg-white/[0.03] border border-white/[0.08]
              text-white placeholder:text-white/30
              focus:outline-none focus:border-white/20 focus:bg-white/[0.05]
              transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              ${error ? 'border-red-500/50 focus:border-red-500/50' : ''}
            `}
          />
        )}

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 rounded-lg bg-zinc-900 border border-white/10 shadow-xl overflow-hidden">
            {search.length < 2 ? (
              <div className="px-4 py-3 text-white/40 text-sm">
                Mindestens 2 Zeichen eingeben...
              </div>
            ) : isLoading ? (
              <div className="px-4 py-3 flex items-center gap-2">
                <LoadingSpinner size="sm" />
                <span className="text-white/50">Suche...</span>
              </div>
            ) : customers && customers.length > 0 ? (
              <ul className="max-h-60 overflow-auto">
                {customers.map((customer) => (
                  <li key={customer.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(customer)}
                      className="w-full px-4 py-2.5 text-left hover:bg-white/5 transition-colors"
                    >
                      <div className="text-white font-medium">
                        {customer.companyName}
                      </div>
                      <div className="text-sm text-white/50">
                        {customer.customerNumber}
                        {customer.email && ` • ${customer.email}`}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-3 text-white/40 text-sm">
                Keine Kunden gefunden
              </div>
            )}
          </div>
        )}
      </div>
      {error && <p className="mt-1.5 text-sm text-red-400">{error}</p>}
    </div>
  );
}
